import { Badge, Chip, IconButton, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import { fetchMessagesUnreadCount } from "../../lib/api/messages";
import { routePaths } from "../../routes/navigation";

function unreadLabel(count: number) {
    if (count === 1) {
        return "1 непрочетено съобщение";
    }

    if (count > 99) {
        return "99+ непрочетени съобщения";
    }

    return `${count} непрочетени съобщения`;
}

export default function SidebarMessagesIndicator({
    collapsed,
    onNavigate,
}: {
    collapsed: boolean;
    onNavigate: (path: string) => void;
}) {
    const user = useAppSelector((state) => state.auth.user);
    const unreadQuery = useQuery({
        queryKey: ["messages-unread-count"],
        queryFn: fetchMessagesUnreadCount,
        enabled: Boolean(user),
        refetchInterval: 30_000,
        retry: 1,
    });

    const unreadCount = unreadQuery.data?.unreadCount ?? 0;

    if (!user || unreadCount <= 0) {
        return null;
    }

    const badgeContent = unreadCount > 99 ? "99+" : unreadCount;
    const openMessages = () => onNavigate(routePaths.messages);

    if (collapsed) {
        return (
            <Tooltip title={unreadLabel(unreadCount)} placement="right" arrow>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={openMessages}
                    aria-label={unreadLabel(unreadCount)}
                    sx={{ alignSelf: "center" }}
                >
                    <Badge color="error" badgeContent={badgeContent} sx={{ "& .MuiBadge-badge": { fontWeight: 800 } }}>
                        <MessageSquare size={18} />
                    </Badge>
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Chip
            icon={<MessageSquare size={16} />}
            label={unreadLabel(unreadCount)}
            color="error"
            size="small"
            clickable
            onClick={openMessages}
            sx={{
                mt: 0.75,
                maxWidth: "100%",
                height: "auto",
                py: 0.25,
                fontWeight: 700,
                "& .MuiChip-label": {
                    whiteSpace: "normal",
                    lineHeight: 1.3,
                },
            }}
        />
    );
}
